import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
         DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import SearchSelect from "@/components/common/SearchSelect";
import { QuickAddItemDialog } from "@/components/QuickAddItemDialog";
import { LastRateHint } from "@/components/LastRateHint";
import { Label } from "@/components/ui/label";
import { fmtINR, fmtDate, today } from "@/lib/format";
import { toast } from "sonner";
import { Plus, Trash2, Download, Receipt, QrCode, Mail, MoreHorizontal, Bell, PenSquare, FileText, Repeat, FileMinus, Copy, ShieldCheck } from "lucide-react";
import { API } from "@/lib/api";
import Attachments from "@/components/Attachments";
import AttachButton from "@/components/AttachButton";
import StagedAttachments from "@/components/StagedAttachments";
import { Switch } from "@/components/ui/switch";
import SendReportButton from "@/components/common/SendReportButton";
import { exportXlsx } from "@/lib/xlsx-export";
import InvoicePreview from "@/components/sales/InvoicePreview";
import DSCSigningPanel from "@/components/DSCSigningPanel";
import ESignButton from "@/components/esign/ESignButton";
import GstinSelector from "@/components/gst/GstinSelector";
import QCRecordModal from "@/components/QCRecordModal";
import FEFOPickerPopover from "@/components/FEFOPickerPopover";
import { CancelInvoiceDialog } from "@/components/sales/CancelInvoiceDialog";
import { InvoiceNumberField, DocNumberField } from "@/components/sales/InvoiceNumberField";
import { PostingPreview } from "@/components/sales/PostingPreview";

const EXPORT_RESET = { is_export: false, is_sez: false, is_service_export: false, export_type: "", lut_reference: "",
  port_code: "", shipping_bill_no: "", shipping_bill_date: "", currency: "INR", fx_rate: 1.0 };
import { InvoiceEmailHistory } from "@/components/sales/InvoiceEmailHistory";
import { tdsOption } from "@/lib/tdsLabel";
import { DraftsPanel, saveDraft, discardDraft } from "@/components/DraftsPanel";

const BLANK_LINE = { item_id: "", description: "", hsn_code: "", qty: 1, rate: 0, discount_pct: 0, gst_rate: 18, unit: "PCS" };
const isServiceItem = (it) => !!(it?.is_service || it?.item_type === "service" || String(it?.hsn_code || "").startsWith("99"));

export default function Sales() {
  const stagedRef = useRef();
  const [invoices, setInvoices] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [suggestedNo, setSuggestedNo] = useState("");
  const [draftId, setDraftId] = useState(null);
  const [draftsKey, setDraftsKey] = useState(0);
  const [openView, setOpenView] = useState(null);
  const [org, setOrg] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [items, setItems] = useState([]);
  const [quickLine, setQuickLine] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewBusy, setPreviewBusy] = useState(false);
  const [branches, setBranches] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [cogsConfig, setCogsConfig] = useState(null);   // Iter-96 · COGS preview
  const [form, setForm] = useState({
    customer_id: "", date: today(), branch_id: "", notes: "",
    entity_gstin: "",   // company-side GSTIN issuing this invoice
    department_id: "",  // Iter-106 · MIS + payment-sheet roll-up
    terms: "Payment due within 30 days.",
    reverse_charge: false, auto_tds: false, tds_section: "", tds_rate: 10,
    // ---- Export / SEZ (Rule 96 / 96A of CGST) ----
    is_export: false, is_sez: false, is_service_export: false,
    export_type: "",       // "" | "with_tax" | "without_tax_lut"
    lut_reference: "", port_code: "", shipping_bill_no: "", shipping_bill_date: "",
    // ---- Foreign currency (AS-11) ----
    currency: "INR", fx_rate: 1.0,
    lines: [{ item_id: "", description: "", hsn_code: "", qty: 1, rate: 0, discount_pct: 0, gst_rate: 18, unit: "PCS" }],
  });
  // Live SEZ verification result from /api/gstin/check-sez — populated when
  // the selected customer's GSTIN is checked against the GST portal.
  const [sezCheck, setSezCheck] = useState(null);
  // Top-of-page GSTIN filter (view scope)
  const [topGstinFilter, setTopGstinFilter] = useState("");
  const [gstConfig, setGstConfig] = useState(null);   // cached org.gst_config for LUT check
  const [tdsSections, setTdsSections] = useState([]);
  const [einvoice, setEinvoice] = useState(null);
  const [qrUrl, setQrUrl] = useState(null);
  const [showReceipt, setShowReceipt] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showReminder, setShowReminder] = useState(false);
  const [showCreditNote, setShowCreditNote] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [canCancel, setCanCancel] = useState(false);
  useEffect(() => { api.get("/sales/invoice-cancel-permission").then((r) => setCanCancel(!!r.data.allowed)).catch(() => {}); }, []);
  const [showRecurring, setShowRecurring] = useState(false);
  const [showQC, setShowQC] = useState(false);

  // Filters
  const [filterMonth, setFilterMonth] = useState("");
  const [filterCustomer, setFilterCustomer] = useState("");
  const [filterWorkflow, setFilterWorkflow] = useState("");

  const load = async () => setInvoices((await api.get("/sales/invoices")).data);

  useEffect(() => {
    load();
    api.get("/auth/me").then((r) => setOrg(r.data.org || {}));
    api.get("/parties", { params: { party_type: "customer" } }).then((r) => setCustomers(r.data));
    api.get("/departments").then((r) => setDepartments(r.data || [])).catch(() => setDepartments([]));
    api.get("/items").then((r) => setItems(r.data));
    api.get("/tds/sections").then((r) => setTdsSections(r.data || [])).catch(() => {});
    api.get("/branches").then((r) => { setBranches(r.data); setForm((f) => ({ ...f, branch_id: r.data[0]?.id || "" })); });
    api.get("/accounts").then((r) => setBankAccounts((r.data || []).filter((a) => a.subtype === "bank" || a.subtype === "cash")));
    api.get("/settings/gst").then((r) => setGstConfig(r.data || {})).catch(() => {});
  }, []);

  // Watch for customer selection changes → auto-detect export / SEZ from the
  // customer master, run a live GST-portal SEZ check for verified status, and
  // reset the export_type radio so the user must consciously pick the mode.
  useEffect(() => {
    if (!form.customer_id) { setSezCheck(null); return; }
    const c = customers.find(x => x.id === form.customer_id);
    if (!c) { setForm(f => ({ ...f, ...EXPORT_RESET })); return; }
    const isExpFromMaster = !!c.is_export_customer;
    const isSezFromMaster = !!c.is_sez_customer || c.gst_status === "sez";
    setForm(f => ({ ...f, is_export: isExpFromMaster, is_sez: isSezFromMaster,
      // If the customer is domestic, wipe any export state that was left behind
      ...(isExpFromMaster || isSezFromMaster ? {} : EXPORT_RESET),
    }));
    if (isSezFromMaster && c.gstin) {
      api.get(`/gstin/check-sez?gstin=${c.gstin}`).then(r => setSezCheck(r.data))
        .catch(() => setSezCheck({ is_sez: false, verified: false, error: "Check failed" }));
    } else { setSezCheck(null); }
  }, [form.customer_id, customers]);

  // Live FX rate fetch on currency change — auto-populates the fx_rate field
  // so the sales register / invoice PDF always shows both foreign + INR amounts.
  useEffect(() => {
    if (form.currency === "INR") { if (form.fx_rate !== 1.0) setForm(f => ({ ...f, fx_rate: 1.0 })); return; }
    api.get(`/fx/rate?currency=${form.currency}&on_date=${form.date}`)
      .then(r => setForm(f => ({ ...f, fx_rate: Number(r.data.rate || 1) })))
      .catch(() => {});
  }, [form.currency, form.date]);

  // Iter-96 · COGS composition for the on-drawer preview
  useEffect(() => {
    api.get("/workflow-config").then((r) => setCogsConfig(r.data?.cogs_composition || []))
      .catch(() => setCogsConfig([]));
  }, []);

  // Derived helpers for the Export/SEZ panel
  const isExportOrSez = form.is_export || form.is_sez;
  const lutValid = !!(gstConfig && (gstConfig.lut_number || gstConfig.lut_arn) &&
    (!gstConfig.lut_valid_to || gstConfig.lut_valid_to >= form.date));

  const setItemLine = (i, itemId, obj) => {
    const it = obj || items.find((x) => x.id === itemId);
    if (!it) return;
    setForm((f) => ({ ...f, lines: f.lines.map((ln, j) => j === i ? {
      ...ln, item_id: itemId, description: it.name, hsn_code: it.hsn_code || "",
      rate: it.sale_price, gst_rate: it.gst_rate, unit: isServiceItem(it) ? "" : (it.unit || "PCS"),
      is_service: isServiceItem(it), ...(isServiceItem(it) ? { qty: 1 } : {}),
    } : ln) }));
  };
  const updateLine = (i, patch) =>
    setForm((f) => ({ ...f, lines: f.lines.map((x, j) => j === i ? { ...x, ...patch } : x) }));

  const compute = (l) => {
    const gross = Number(l.qty || 0) * Number(l.rate || 0);
    const disc = gross * Number(l.discount_pct || 0) / 100;
    const base = gross - disc;
    const tax = base * Number(l.gst_rate || 0) / 100;
    return { gross, disc, base, tax, total: base + tax };
  };
  const totals = form.lines.reduce((acc, l) => {
    const c = compute(l);
    acc.sub += c.base; acc.tax += c.tax; acc.discount += c.disc; acc.grand += c.total;
    return acc;
  }, { sub: 0, tax: 0, discount: 0, grand: 0 });

  // Iter-96/97 · Live COGS preview using items[].cost_price × qty and workflow_config uplifts
  const cogsPreview = useMemo(() => {
    let goods = 0;
    for (const ln of form.lines) {
      const it = items.find((x) => x.id === ln.item_id);
      if (!it || it.is_service) continue;
      const uc = Number(it.cost_price || it.purchase_price || 0);
      goods += uc * Number(ln.qty || 0);
    }
    const enabledUplifts = (cogsConfig || []).filter((c) => c.enabled && c.key !== "goods");
    const uplifts = enabledUplifts.map((c) => ({
      key: c.key, label: c.label, pct: Number(c.uplift_pct || 0),
      amount: (goods * Number(c.uplift_pct || 0)) / 100,
    }));
    const total = goods + uplifts.reduce((s, u) => s + u.amount, 0);
    const totalPct = uplifts.reduce((s, u) => s + u.pct, 0);
    return { goods, uplifts, total, totalPct };
  }, [form.lines, items, cogsConfig]);

  const draftInvoice = useMemo(() => {
    const cust = customers.find((c) => c.id === form.customer_id) || {};
    const intra = !!org?.state && org.state === (form.place_of_supply_override || cust.state);
    const lines = form.lines.map((l) => {
      const c = compute(l);
      const half = Math.round((c.tax / 2) * 100) / 100;
      return { ...l, taxable_amount: c.base, cgst: intra ? half : 0, sgst: intra ? half : 0, igst: intra ? 0 : c.tax, total: c.total };
    });
    const subtotal = lines.reduce((s, l) => s + l.taxable_amount, 0);
    const grand = lines.reduce((s, l) => s + l.total, 0);
    const tds = form.auto_tds && form.tds_section ? Math.round(subtotal * Number(form.tds_rate || 0)) / 100 : 0;
    return { ...form, lines, invoice_number: form.invoice_number || suggestedNo || "(on posting)", place_of_supply: form.place_of_supply_override || cust.state, grand_total: grand, tds_amount: tds };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, customers, org, suggestedNo]);

  const openPdfPreview = async () => {
    if (!form.customer_id) return toast.error("Select a customer");
    setPreviewBusy(true);
    try {
      const r = await api.post("/sales/invoices/preview-pdf", draftInvoice, { responseType: "blob" });
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(URL.createObjectURL(new Blob([r.data], { type: "application/pdf" })));
      setPreviewOpen(true);
    } catch (e) {
      let msg = "Preview failed";
      try { msg = JSON.parse(await e.response.data.text()).detail || msg; } catch { /* keep default */ }
      toast.error(msg);
    } finally { setPreviewBusy(false); }
  };

  const submit = async () => {
    if (!form.customer_id) return toast.error("Select a customer");
    if (form.lines.length === 0) return toast.error("Add at least one line");
    // Export / SEZ gating — must match backend rules or the API will reject.
    if (isExportOrSez) {
      if (!form.export_type) return toast.error("Select 'With payment of tax' or 'Without payment of tax (LUT)'");
      if (form.export_type === "without_tax_lut") {
        if (!lutValid) return toast.error("No valid LUT on file. Set it in Settings → GST Config → LUT.");
        if (form.lines.some(l => Number(l.gst_rate || 0) > 0))
          return toast.error("Every line's GST% must be 0 when exporting under LUT.");
      }
    }
    try {
      const res = await api.post("/sales/invoices", {
        ...form, place_of_supply: form.place_of_supply_override || "",
        lines: form.lines.map((l) => ({ ...l, discount_pct: Number(l.discount_pct || 0) })),
      });
      await stagedRef.current?.flush("sales_invoice", res.data.id);
      await discardDraft(draftId); setDraftId(null); setDraftsKey((k) => k + 1);
      toast.success("Invoice posted");
      setOpenNew(false);
      setForm((f) => ({ ...f, customer_id: "", narration: "", auto_tds: false, tds_section: "", invoice_number: "", place_of_supply_override: "", ...EXPORT_RESET,
        lines: [{ item_id: "", description: "", hsn_code: "", qty: 1, rate: 0, discount_pct: 0, gst_rate: 18, unit: "PCS" }] }));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const openDetail = async (id) => setOpenView((await api.get(`/sales/invoices/${id}`)).data);

  const downloadPDF = async (id, copy = "original") => {
    const res = await fetch(`${API}/sales/invoices/${id}/cgst-invoice.pdf?copy=${copy}`, { credentials: "include" });
    if (!res.ok) return toast.error("PDF failed");
    const blob = await res.blob(); const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const suffix = copy === "original" ? "" : `-${copy}`;
    a.href = url; a.download = `Invoice-${openView?.invoice_number || id.slice(0, 8)}${suffix}.pdf`; a.click();
    URL.revokeObjectURL(url);
  };

  const loadQr = async (invId) => {
    if (qrUrl) { URL.revokeObjectURL(qrUrl); setQrUrl(null); }
    try {
      const res = await fetch(`${API}/einvoice/qr/${invId}.png`, { credentials: "include" });
      if (!res.ok) return;
      const blob = await res.blob();
      setQrUrl(URL.createObjectURL(blob));
    } catch (e) {
      console.warn("[Sales] IRN QR fetch failed:", e?.message || e);
    }
  };

  const filteredInvoices = useMemo(() => invoices.filter((i) => {
    if (topGstinFilter && i.entity_gstin && i.entity_gstin !== topGstinFilter) return false;
    if (filterMonth && !(i.date || "").startsWith(filterMonth)) return false;
    if (filterCustomer && i.customer_id !== filterCustomer) return false;
    if (filterWorkflow && (i.workflow_status || "draft") !== filterWorkflow) return false;
    return true;
  }), [invoices, filterMonth, filterCustomer, filterWorkflow]);

  const setWorkflow = async (id, status) => {
    try {
      await api.patch(`/sales/invoices/${id}/workflow-status`, { status });
      toast.success(`Marked ${status.replaceAll("_", " ")}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const exportCsv = () => {
    const rows = filteredInvoices.map((i) => [i.invoice_number, i.date, i.customer_name, i.entity_gstin || "", i.subtotal, i.tax_total, i.grand_total, i.balance_due, i.status, i.workflow_status || "draft"]);
    const csv = ["Invoice#,Date,Customer,Entity GSTIN,Subtotal,GST,Total,Due,Status,Workflow", ...rows.map((r) => r.map((c) => `"${c ?? ""}"`).join(","))].join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = `sales_${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };
  const exportXlsxHandler = () => {
    exportXlsx({
      filename: `sales_${new Date().toISOString().slice(0, 10)}.xlsx`,
      sheetName: "Sales Invoices",
      columns: [
        { key: "invoice_number",  label: "Invoice #" },
        { key: "date",             label: "Date", format: "date" },
        { key: "customer_name",   label: "Customer" },
        { key: "entity_gstin",    label: "Entity GSTIN" },
        { key: "subtotal",        label: "Subtotal" },
        { key: "tax_total",       label: "GST" },
        { key: "grand_total",     label: "Total" },
        { key: "balance_due",     label: "Balance Due" },
        { key: "status",          label: "Status" },
        { key: "workflow_status", label: "Workflow" },
      ],
      rows: filteredInvoices.map((i) => ({ ...i, workflow_status: i.workflow_status || "draft" })),
    });
  };

  const genEInvoice = async (invId) => {
    try {
      const r = await api.post(`/einvoice/generate/${invId}`);
      toast.success(`E-Invoice IRN generated · ${r.data.irn?.slice(0, 12)}…`);
      await loadEInvoice(invId); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "IRN generation failed"); }
  };
  const genEwayBill = async (invId) => {
    const vehicleNo = window.prompt("Vehicle number (e.g. MH01AB1234):", "");
    if (!vehicleNo) return;
    const distStr = window.prompt("Approximate distance to destination (km):", "100");
    const distance = parseInt(distStr || "100", 10) || 100;
    try {
      const r = await api.post(`/eway-bill/generate/${invId}`, {
        vehicle_no: vehicleNo, distance_km: distance, transport_mode: "1", vehicle_type: "R",
      });
      toast.success(`E-Way Bill generated · ${r.data.ewb_no}`);
      await loadEInvoice(invId); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "E-Way Bill generation failed"); }
  };
  const cancelEInvoice = async (invId) => {
    if (!window.confirm("Cancel this IRN? This action reports to the NIC IRP and cannot be reversed after 24h.")) return;
    try {
      await api.post(`/einvoice/cancel/${invId}`, { reason_code: "2", reason_note: "Data Entry Mistake" });
      toast.success("IRN cancelled"); await loadEInvoice(invId);
    } catch (e) { toast.error(e?.response?.data?.detail || "Cancel failed"); }
  };
  const loadEInvoice = async (invId) => {
    try {
      const r = await api.get(`/einvoice/status/${invId}`);
      setEinvoice(r.data);
      if (r.data?.signed_qr_code) await loadQr(invId); else setQrUrl(null);
    } catch (e) {
      console.warn("[Sales] E-Invoice status fetch failed:", e?.message || e);
      setEinvoice(null);
      setQrUrl(null);
    }
  };
  useEffect(() => {
    if (openView) loadEInvoice(openView.id);
    else { setEinvoice(null); if (qrUrl) { URL.revokeObjectURL(qrUrl); setQrUrl(null); } }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openView]);

  /** Duplicate Last Invoice (Feb 2026) — clones the most recent posted invoice
   *  into a new draft with today's date and a fresh number. Also triggerable
   *  via keyboard Alt+D. */
  const duplicateLast = async () => {
    try {
      const r = await api.post("/sales/invoices/duplicate-last", {});
      toast.success(`Cloned ${r.data.cloned_from} → ${r.data.invoice.invoice_number}`);
      // Refresh list + open the new draft in the edit sheet
      const list = (await api.get("/sales/invoices")).data;
      setInvoices(list);
      const newInv = list.find(i => i.id === r.data.invoice.id);
      if (newInv) setOpenView(newInv);
    } catch (e) { toast.error(e?.response?.data?.detail || "Nothing to duplicate"); }
  };
  useEffect(() => {
    const h = (e) => { if (e.altKey && e.key.toLowerCase() === "d") { e.preventDefault(); duplicateLast(); } };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="sales-page">
      <DraftsPanel docType="sales_invoice" refreshKey={draftsKey} onPosted={() => load()}
        onOpen={(d) => { setForm(d.ui); setDraftId(d.id); setOpenNew(true); }} />
      <PageHeader
        eyebrow="Sales"
        title="Sales Invoices"
        description="GST-compliant tax invoices with per-line discounts, RCM flag, CGST/SGST/IGST split, bank details and authorised signatory."
        actions={
          <div className="flex items-center gap-2">
            <Button onClick={duplicateLast} variant="outline"
                      className="rounded-sm gap-1.5 h-9" data-testid="duplicate-last-invoice-btn"
                      title="Clone the most recent invoice into a new draft — Alt+D">
              <Copy className="w-3.5 h-3.5" /> Duplicate last
            </Button>
            <Button onClick={() => setOpenNew(true)} className="rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="new-invoice-btn">+ Invoice</Button>
          </div>
        }
      />

      {/* Filters */}
      <div className="card-sharp p-3 flex flex-wrap items-center gap-3">
        <GstinSelector value={{ gstin: topGstinFilter }}
                          onChange={(g) => setTopGstinFilter(g?.gstin || "")}
                          label="GSTIN Scope" testid="sales-top-gstin-filter" />
        <input type="month" value={filterMonth} onChange={(e) => setFilterMonth(e.target.value)} className="rounded-sm h-8 border border-input bg-background px-2 text-sm" data-testid="sales-filter-month" />
        <SearchSelect options={[{ value: "", label: "All customers" }, ...customers.map((c) => ({ value: c.id, label: c.name }))]} value={filterCustomer} onChange={setFilterCustomer} testId="sales-filter-cust" placeholder="Filter by customer" className="w-56 h-8" />
        <select value={filterWorkflow} onChange={(e) => setFilterWorkflow(e.target.value)} className="rounded-sm h-8 border border-input bg-background px-2 text-sm" data-testid="sales-filter-workflow">
          <option value="">All workflow states</option>
          {["draft","for_approval","approved","e_invoiced","e_way_billed","signed","sent","paid","unpaid"].map((s) => <option key={s} value={s}>{s.replaceAll("_"," ").toUpperCase()}</option>)}
        </select>
        <div className="flex-1" />
        <SendReportButton reportLabel="Sales Register" defaultSubject="Sales invoices summary — Zenith 360" />
        <Button variant="outline" className="rounded-sm h-8 gap-1.5" onClick={exportCsv} data-testid="sales-export-csv">Export CSV</Button>
        <Button variant="outline" className="rounded-sm h-8 gap-1.5" onClick={exportXlsxHandler} data-testid="sales-export-xlsx">Export XLSX</Button>
        <div className="text-xs text-muted-foreground">{filteredInvoices.length} of {invoices.length} shown</div>
      </div>

      <div className="card-sharp overflow-hidden">
        <table className="w-full dense">
          <thead><tr><th>Date</th><th>Invoice #</th><th>Customer</th><th className="text-xs">GSTIN</th><th className="text-right">Subtotal</th><th className="text-right">GST</th><th className="text-right">Total</th><th className="text-right">Due</th><th>Status</th><th>Workflow</th><th></th></tr></thead>
          <tbody>
            {filteredInvoices.length === 0 && <tr><td colSpan="11" className="text-center text-muted-foreground py-8">No invoices match the current filters</td></tr>}
            {filteredInvoices.map((i) => (
              <tr key={i.id} className="cursor-pointer" data-testid={`inv-row-${i.invoice_number}`}>
                <td onClick={() => openDetail(i.id)} className="text-xs">{fmtDate(i.date)}</td>
                <td onClick={() => openDetail(i.id)} className="font-mono text-xs">
                  {i.invoice_number}
                  {i.source_ref && <div className="text-[10px] text-muted-foreground normal-case">from {i.source_type === "quote" ? "Quote" : "SO"} {i.source_ref}</div>}
                </td>
                <td onClick={() => openDetail(i.id)}>{i.customer_name}</td>
                <td className="text-xs font-mono text-muted-foreground">{i.entity_gstin || "—"}</td>
                <td className="text-right tabular-nums">{fmtINR(i.subtotal)}</td>
                <td className="text-right tabular-nums text-muted-foreground">{fmtINR(i.tax_total)}</td>
                <td className="text-right tabular-nums font-medium">
                  {fmtINR(i.grand_total)}
                  {i.currency && i.currency !== "INR" && (
                    <div className="text-[9px] font-mono text-muted-foreground normal-case">
                      {i.currency} {Number(i.currency_amount || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      <span className="ml-1">· @{Number(i.fx_rate || 1).toFixed(4)}</span>
                    </div>
                  )}
                </td>
                <td onClick={() => openDetail(i.id)} className="text-right tabular-nums">{fmtINR(i.balance_due)}</td>
                <td onClick={() => openDetail(i.id)}><span className={`text-[10px] font-mono uppercase tracking-widest ${i.status === "paid" ? "text-emerald-600" : i.status === "cancelled" ? "text-red-600 line-through" : "text-accent"}`}>{i.status}</span></td>
                <td>
                  <select value={i.workflow_status || "draft"}
                          onChange={(e) => setWorkflow(i.id, e.target.value)}
                          onClick={(e) => e.stopPropagation()}
                          data-testid={`inv-workflow-${i.invoice_number}`}
                          className={`text-[10px] font-mono uppercase tracking-wider rounded-sm border px-1.5 py-0.5 cursor-pointer
                            ${(i.workflow_status || "draft") === "paid" ? "bg-emerald-100 text-emerald-700 border-emerald-300" :
                              (i.workflow_status || "draft") === "sent" ? "bg-blue-100 text-blue-700 border-blue-300" :
                              (i.workflow_status || "draft") === "approved" ? "bg-purple-100 text-purple-700 border-purple-300" :
                              (i.workflow_status || "draft") === "for_approval" ? "bg-amber-100 text-amber-700 border-amber-300" :
                              (i.workflow_status || "draft") === "unpaid" ? "bg-red-100 text-red-700 border-red-300" :
                              (i.workflow_status || "draft") === "signed" ? "bg-teal-100 text-teal-700 border-teal-300" :
                              ["e_invoiced", "e_way_billed"].includes(i.workflow_status) ? "bg-sky-100 text-sky-700 border-sky-300" :
                              "bg-gray-100 text-gray-700 border-gray-300"}`}>
                    {["draft","for_approval","approved","e_invoiced","e_way_billed","signed","sent","paid","unpaid"].map((s) =>
                      <option key={s} value={s}>{s.replaceAll("_"," ").toUpperCase()}</option>)}
                  </select>
                </td>
                <td className="text-right"><AttachButton docType="sales_invoice" docId={i.id} docNumber={i.invoice_number} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* NEW INVOICE DRAWER */}
      <Sheet open={openNew} onOpenChange={(o) => { setOpenNew(o); if (!o && draftId) { setDraftId(null); setForm((f) => ({ ...f, customer_id: "", narration: "", auto_tds: false, tds_section: "", invoice_number: "", ...EXPORT_RESET, lines: [{ ...BLANK_LINE }] })); } }}>
        <SheetContent side="right" className="w-full sm:max-w-6xl rounded-none overflow-y-auto" data-testid="new-invoice-drawer">
          <SheetHeader className="pb-4 border-b">
            <SheetTitle className="font-heading">New Sales Invoice</SheetTitle>
            <div className="pt-2">
              <GstinSelector
                value={{ gstin: form.entity_gstin }}
                onChange={(g) => setForm({ ...form, entity_gstin: g?.gstin || "" })}
                label="Issue from GSTIN"
                showLegalName
                testid="sales-entity-gstin"
              />
            </div>
          </SheetHeader>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
            <InvoiceNumberField open={openNew} branchId={form.branch_id} value={form.invoice_number}
              onChange={(v) => setForm((f) => ({ ...f, invoice_number: v }))} onSuggested={setSuggestedNo} />
            <div>
              <Label className="text-[10px] uppercase font-mono tracking-widest">Customer</Label>
              <SearchSelect options={customers.map((c) => ({ value: c.id, label: c.name, sublabel: c.gstin }))} value={form.customer_id} onChange={(v) => setForm({ ...form, customer_id: v })} testId="inv-customer" placeholder="Select customer" />
            </div>
            <div>
              <Label className="text-[10px] uppercase font-mono tracking-widest">Branch</Label>
              <Select value={form.branch_id} onValueChange={(v) => {
                const br = branches.find((b) => b.id === v);
                setForm({ ...form, branch_id: v,
                             entity_gstin: br?.gstin || form.entity_gstin });
              }}>
                <SelectTrigger className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{branches.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase font-mono tracking-widest">Date</Label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-sm mt-1" data-testid="inv-date" />
            </div>
            {departments.length > 0 && (
              <div>
                <Label className="text-[10px] uppercase font-mono tracking-widest">Department</Label>
                <Select value={form.department_id || "__none__"} onValueChange={(v) => setForm({ ...form, department_id: v === "__none__" ? "" : v })}>
                  <SelectTrigger className="rounded-sm mt-1" data-testid="inv-department"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">—</SelectItem>
                    {departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.code} · {d.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div className="mt-6 border rounded-sm overflow-x-auto" data-testid="inv-lines-scroll">
            <table className="w-full dense min-w-[900px] table-fixed [&_td]:!px-1 [&_td_input]:px-2 [&_td_input]:!min-w-0">
              <thead>
                <tr>
                  <th>Item / Description</th><th style={{ width: 100 }}>HSN/SAC</th>
                  <th style={{ width: 72 }} className="text-right">Qty</th><th style={{ width: 66 }}>Unit</th>
                  <th style={{ width: 110 }} className="text-right">Rate</th>
                  <th style={{ width: 70 }} className="text-right">Disc %</th>
                  <th style={{ width: 66 }} className="text-right">GST %</th>
                  <th style={{ width: 120 }} className="text-right">Total</th><th style={{ width: 36 }}></th>
                </tr>
              </thead>
              <tbody>
                {form.lines.map((l, i) => {
                  const c = compute(l);
                  return (
                    <tr key={i}>
                      <td className="p-1 space-y-1">
                        <SearchSelect
                          options={[{ value: "__new__", label: "+ Create new item / service" }, { value: "custom", label: "— Custom (no item link) —" }, ...items.map((it) => ({ value: it.id, label: it.name, sublabel: it.sku }))]}
                          value={l.item_id || "custom"}
                          onChange={(v) => { if (v === "__new__") setQuickLine(i); else if (v !== "custom") setItemLine(i, v); }}
                          testId={`inv-item-${i}`}
                          placeholder="Pick item / custom"
                          className="h-8 text-xs" />
                        {i === 0 && (
                          <QuickAddItemDialog open={quickLine !== null} onOpenChange={(o) => !o && setQuickLine(null)} side="sales"
                            onCreated={(it) => { setItems((p) => [...p, it]); setItemLine(quickLine, it.id, it); }} />
                        )}
                        <Input value={l.description} onChange={(e) => updateLine(i, { description: e.target.value })} className="rounded-sm h-8 text-xs" placeholder="Description" data-testid={`inv-desc-${i}`} />
                        <Textarea value={l.line_note || ""} onChange={(e) => updateLine(i, { line_note: e.target.value })} rows={1}
                          className="rounded-sm text-xs min-h-[32px] py-1" placeholder="Narration / details for this line (prints below the item)" data-testid={`inv-line-note-${i}`} />
                        <LastRateHint itemId={l.item_id} partyId={form.customer_id} side="sales" currentRate={l.rate} testId={`inv-last-rate-${i}`}
                          onApply={(r) => updateLine(i, { rate: r.rate, discount_pct: r.discount_pct || 0 })} />
                        {/* Iter-103 · FEFO/FIFO popover on outward line */}
                        {l.item_id && (
                          <div className="flex items-center gap-2">
                            <FEFOPickerPopover
                              itemId={l.item_id}
                              qty={Number(l.qty) || 1}
                              onPick={(p) => updateLine(i, { batch_no: p.batch_no, rate: l.rate || p.unit_cost })}
                            />
                            {l.batch_no && (
                              <span className="text-[10px] font-mono text-muted-foreground" data-testid={`inv-batch-${i}`}>
                                batch: {l.batch_no}
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="p-1"><Input value={l.hsn_code || ""} onChange={(e) => updateLine(i, { hsn_code: e.target.value, ...(!l.item_id ? { is_service: e.target.value.startsWith("99"), ...(e.target.value.startsWith("99") ? { qty: 1, unit: "" } : {}) } : {}) })} className="rounded-sm h-8 text-xs" placeholder="HSN / SAC" data-testid={`inv-hsn-${i}`} /></td>
                      {l.is_service ? (
                        <td colSpan={2} className="p-1 text-center text-[10px] text-muted-foreground" data-testid={`inv-service-${i}`}>Service · no qty</td>
                      ) : (<>
                      <td className="p-1"><Input type="number" value={l.qty} onChange={(e) => updateLine(i, { qty: Number(e.target.value) })} className="rounded-sm h-8 text-right tabular-nums" data-testid={`inv-qty-${i}`} /></td>
                      <td className="p-1"><Input value={l.unit || "PCS"} onChange={(e) => updateLine(i, { unit: e.target.value })} className="rounded-sm h-8 text-xs" data-testid={`inv-unit-${i}`} /></td>
                      </>)}
                      <td className="p-1"><Input type="number" value={l.rate} onChange={(e) => updateLine(i, { rate: Number(e.target.value) })} className="rounded-sm h-8 text-right tabular-nums" data-testid={`inv-rate-${i}`} /></td>
                      <td className="p-1"><Input type="number" step="0.1" value={l.discount_pct || 0} onChange={(e) => updateLine(i, { discount_pct: Number(e.target.value) })} className="rounded-sm h-8 text-right tabular-nums" data-testid={`inv-disc-${i}`} /></td>
                      <td className="p-1"><Input type="number" value={l.gst_rate} onChange={(e) => updateLine(i, { gst_rate: Number(e.target.value) })} className="rounded-sm h-8 text-right tabular-nums" data-testid={`inv-gst-${i}`} /></td>
                      <td className="text-right tabular-nums text-sm">
                        {fmtINR(c.total)}
                        {c.disc > 0 && <div className="text-[9px] text-muted-foreground">save {fmtINR(c.disc)}</div>}
                      </td>
                      <td className="text-center"><button type="button" title="Delete line" data-testid={`inv-del-line-${i}`} onClick={() => setForm((f) => ({ ...f, lines: f.lines.length > 1 ? f.lines.filter((_, j) => j !== i) : [{ ...BLANK_LINE }] }))} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="border-t p-3 bg-muted/40 flex items-center justify-between">
              <Button variant="ghost" size="sm" className="rounded-sm gap-1" data-testid="inv-add-line"
                      onClick={() => setForm((f) => ({ ...f, lines: [...f.lines, { item_id: "", description: "", hsn_code: "", qty: 1, rate: 0, discount_pct: 0, gst_rate: 18, unit: "PCS" }] }))}>
                <Plus className="w-3.5 h-3.5" /> Add line
              </Button>
              <div className="flex gap-6 text-sm tabular-nums">
              </div>
              {/* Feb 2026 · Live GST preview widget — updates on every line edit */}
              <div className="mt-3 p-3 rounded-sm border border-accent/25 bg-accent/5" data-testid="invoice-gst-preview">
                <div className="text-[10px] font-mono uppercase tracking-widest text-accent mb-1.5">// Live tax breakup</div>
                {(() => {
                  const byRate = {};
                  let subtotal = 0;
                  form.lines.forEach(l => {
                    const qty  = Number(l.qty) || 0;
                    const rate = Number(l.rate) || 0;
                    const disc = Number(l.discount_pct) || 0;
                    const line = qty * rate * (1 - disc / 100);
                    const gst  = Number(l.gst_rate) || 0;
                    subtotal += line;
                    if (!byRate[gst]) byRate[gst] = { taxable: 0, tax: 0 };
                    byRate[gst].taxable += line;
                    byRate[gst].tax     += line * gst / 100;
                  });
                  const orgStateCode = (form.entity_gstin || "").slice(0, 2);
                  const posCode = (customers.find(c => c.id === form.customer_id) || {})
                                      .state_code || (form.entity_gstin || "").slice(0, 2);
                  const posIntra = form.place_of_supply_override ? org?.state === form.place_of_supply_override : null;
                  const isIntra = form.is_export || form.is_sez ? false : (posIntra !== null ? posIntra : (orgStateCode && posCode ? orgStateCode === posCode : true));
                  const totalTax = Object.values(byRate).reduce((a, b) => a + b.tax, 0);
                  return (
                    <div className="text-[11px] space-y-1">
                      {Object.keys(byRate).sort((a, b) => Number(a) - Number(b)).map(g => (
                        <div key={g} className="flex justify-between font-mono">
                          <span>@ {g}% on {fmtINR(byRate[g].taxable)}</span>
                          {isIntra
                            ? <span>CGST {fmtINR(byRate[g].tax / 2)} + SGST {fmtINR(byRate[g].tax / 2)}</span>
                            : <span>IGST {fmtINR(byRate[g].tax)}</span>}
                        </div>
                      ))}
                      <div className="flex justify-between font-mono pt-1 mt-1 border-t border-accent/20">
                        <span>Subtotal · Tax · Grand</span>
                        <span className="font-semibold">{fmtINR(subtotal)} · {fmtINR(totalTax)} · {fmtINR(subtotal + totalTax)}</span>
                      </div>
                      <div className="text-[10px] text-muted-foreground">
                        {form.is_export ? "Export — LUT / IGST auto" : form.is_sez ? "SEZ supply" : (isIntra ? "Intra-state · CGST + SGST" : "Inter-state · IGST")}
                      </div>
                    </div>
                  );
                })()}
              </div>
              <div className="hidden">Old totals block continues below…</div>
              <div className="text-right space-y-1 text-sm border-t border-border/60 pt-3 mt-3">
                {totals.discount > 0 && <div className="text-emerald-700">Discount: <span className="font-mono">-{fmtINR(totals.discount)}</span></div>}
                <div>Subtotal: <span className="font-mono">{fmtINR(totals.sub)}</span></div>
                <div>GST: <span className="font-mono">{fmtINR(totals.tax)}</span></div>
                <div className="font-semibold">Grand: <span className="font-mono">{fmtINR(totals.grand)}</span></div>
              </div>

              {/* Iter-96 · Live COGS preview */}
              {cogsPreview.goods > 0 && (
                <div className="mt-3 border border-amber-500/30 bg-amber-500/10 rounded-sm p-3 text-xs" data-testid="cogs-preview">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-amber-800">// COGS preview (before post)</div>
                    <div className="text-[10px] text-amber-700">DR 5210 · CR 1300</div>
                  </div>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                    <div className="text-amber-800">Goods (item cost × qty)</div>
                    <div className="text-right font-mono tabular-nums text-amber-900">{fmtINR(cogsPreview.goods)}</div>
                    {cogsPreview.uplifts.map((u) => (
                      <React.Fragment key={u.key}>
                        <div className="text-amber-700">+ {u.label} <span className="font-mono">({u.pct}%)</span></div>
                        <div className="text-right font-mono tabular-nums text-amber-800">{fmtINR(u.amount)}</div>
                      </React.Fragment>
                    ))}
                  </div>
                  <div className="border-t border-amber-500/40 mt-2 pt-2 flex items-center justify-between">
                    <div className="text-amber-900 font-medium">Total COGS entry <span className="text-[10px] font-mono ml-1 opacity-80">(uplift {cogsPreview.totalPct}%)</span></div>
                    <div className="font-mono tabular-nums font-semibold text-amber-900" data-testid="cogs-preview-total">{fmtINR(cogsPreview.total)}</div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {isExportOrSez && (
            <div className="mt-6 border border-amber-300 bg-amber-50 rounded-sm p-3" data-testid="inv-export-panel">
              <div className="text-sm font-medium">{form.is_sez ? "SEZ supply" : "Export supply"} — customer master is marked {form.is_sez ? "SEZ" : "export"}</div>
              <div className="text-[11px] text-muted-foreground mb-2">If this customer is domestic, correct the customer master (GST status) instead.</div>
              <div className="flex flex-wrap gap-4 text-sm">
                {[["with_tax", "With payment of IGST"], ["without_tax_lut", "Without payment of tax (LUT)"]].map(([v, l]) => (
                  <label key={v} className="flex items-center gap-2 cursor-pointer">
                    <input type="radio" name="export_type" checked={form.export_type === v}
                      onChange={() => setForm((f) => ({ ...f, export_type: v }))} data-testid={`inv-export-${v}`} />{l}
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="mt-6 grid md:grid-cols-2 gap-3">
            <div className="border rounded-sm p-3 bg-muted/30">
              <label className="flex items-center gap-3 cursor-pointer">
                <Switch checked={form.reverse_charge} onCheckedChange={(v) => setForm({ ...form, reverse_charge: v })} data-testid="inv-rcm" />
                <div>
                  <div className="text-sm font-medium">Reverse Charge Mechanism (RCM)</div>
                  <div className="text-[10px] text-muted-foreground">Prints "YES" on the invoice — recipient will discharge GST liability.</div>
                </div>
              </label>
            </div>
            <div className="border rounded-sm p-3 bg-muted/30 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-xs">Auto-deduct TDS / Collect TCS</Label>
                  <div className="text-[10px] text-muted-foreground mt-0.5">Customer deducts TDS → posts TDS Receivable Dr · Seller collects TCS → posts TCS Payable Cr</div>
                </div>
                <Switch checked={form.auto_tds} onCheckedChange={(v) => setForm({ ...form, auto_tds: v })} data-testid="inv-auto-tds" />
              </div>
              {form.auto_tds && (
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-[10px]">Section</Label>
                    <div className="mt-1"><SearchSelect
                      options={tdsSections.map(tdsOption)}
                      wrap large contentClassName="w-[min(44rem,94vw)]"
                      value={form.tds_section}
                      onChange={(v) => { const s = tdsSections.find((x) => x.section === v); setForm((f) => ({ ...f, tds_section: v, tds_rate: s?.rate ?? f.tds_rate })); }}
                      placeholder="Type 393, code 1027, nature or old 194J"
                      testId="inv-tds-section" className="h-10 text-sm" /></div>
                  </div>
                  <div><Label className="text-[10px]">Rate %</Label><Input type="number" value={form.tds_rate} onChange={(e) => setForm({ ...form, tds_rate: Number(e.target.value) })} className="rounded-sm h-8 mt-1 text-right tabular-nums" data-testid="inv-tds-rate" /></div>
                </div>
              )}
            </div>
          </div>

          <div className="mt-6"><Label>Narration</Label>
            <Textarea value={form.narration || ""} onChange={(e) => setForm({ ...form, narration: e.target.value })} placeholder="Shown in the ledger / day book for this invoice" className="rounded-sm mt-1 min-h-[60px]" data-testid="inv-narration" />
          </div>
          <div className="mt-6 flex justify-end gap-2">
            <Button variant="outline" onClick={() => setOpenNew(false)} className="rounded-sm">Cancel</Button>
            <Button variant="outline" data-testid="inv-save-draft" className="rounded-sm" onClick={async () => {
              if (!form.customer_id) return toast.error("Select a customer");
              try {
                await saveDraft("sales_invoice", { ...form, lines: form.lines.map((l) => ({ ...l, discount_pct: Number(l.discount_pct || 0) })) }, form, draftId);
                setDraftId(null); setOpenNew(false); setDraftsKey((k) => k + 1);
                setForm((f) => ({ ...f, customer_id: "", narration: "", auto_tds: false, tds_section: "", lines: [{ ...BLANK_LINE }] }));
              } catch (e) { toast.error(e?.response?.data?.detail || "Could not save draft"); }
            }}>{draftId ? "Update Draft" : "Save as Draft"}</Button>
            <Button variant="outline" onClick={openPdfPreview} disabled={previewBusy} className="rounded-sm gap-1.5" data-testid="inv-preview-btn"><FileText className="w-3.5 h-3.5" /> {previewBusy ? "Building…" : "Preview PDF"}</Button>
            <Button onClick={submit} className="rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="inv-submit">Post Invoice</Button>
          </div>
          <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
            <DialogContent className="rounded-sm max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="inv-preview-dialog">
              <DialogHeader><DialogTitle className="font-heading">Invoice PDF preview · not posted yet</DialogTitle></DialogHeader>
              <PostingPreview draft={{ ...draftInvoice, place_of_supply_override: form.place_of_supply_override }}
                onChangeLine={(i, v) => updateLine(i, { income_account_id: v })}
                onChangePos={(v) => setForm((f) => ({ ...f, place_of_supply_override: v }))} onRefreshPdf={openPdfPreview} />
              {form.place_of_supply_override && <div className="text-xs text-amber-700" data-testid="posting-pos-note">Place of supply changed — click Refresh PDF to update the PDF tax split.</div>}
              {previewUrl && <iframe src={previewUrl} title="Invoice PDF preview" className="w-full h-[70vh] border rounded-sm bg-white" data-testid="inv-preview-pdf" />}
              <div className="flex justify-end gap-2 mt-3">
                <Button variant="outline" onClick={() => setPreviewOpen(false)} className="rounded-sm" data-testid="inv-preview-edit">Back to edit</Button>
                <Button onClick={async () => { setPreviewOpen(false); await submit(); }} className="rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="inv-preview-post">Post Invoice</Button>
              </div>
            </DialogContent>
          </Dialog>
          <div className="mt-4"><StagedAttachments ref={stagedRef} label="Attach supporting documents (uploads on save)" /></div>
        </SheetContent>
      </Sheet>

      <CancelInvoiceDialog invoice={openView} open={showCancel} onOpenChange={setShowCancel}
        onDone={() => { load(); openDetail(openView.id); }} />
      {/* VIEW INVOICE DRAWER — with single Actions dropdown + on-screen preview */}
      <Sheet open={!!openView} onOpenChange={(o) => !o && setOpenView(null)}>
        <SheetContent side="right" className="w-full sm:max-w-3xl rounded-none overflow-y-auto p-0" data-testid="invoice-view-drawer">
          {openView && (
            <div>
              {/* Sticky action bar */}
              <div className="sticky top-0 z-10 bg-white border-b px-6 py-3 flex items-center justify-between gap-3">
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Tax Invoice</div>
                  <div className="font-mono text-lg">{openView.invoice_number}</div>
                  {openView.status === "cancelled" && (
                    <div className="text-[11px] text-red-600 font-medium" data-testid="invoice-cancelled-banner">CANCELLED · {openView.cancel_reason} · by {openView.cancelled_by_name} on {fmtDate(openView.cancelled_at)}</div>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">{fmtDate(openView.date)}</span>

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button size="sm" className="rounded-sm gap-1.5 bg-accent hover:bg-accent/90 text-white" data-testid="invoice-actions-btn">
                        Actions <MoreHorizontal className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-56" data-testid="invoice-actions-menu">
                      <DropdownMenuLabel>Invoice actions</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => downloadPDF(openView.id, "original")} data-testid="act-pdf">
                        <Download className="w-4 h-4 mr-2" /> PDF · Original
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => downloadPDF(openView.id, "duplicate")} data-testid="act-pdf-dup">
                        <Copy className="w-4 h-4 mr-2" /> PDF · Duplicate (Transporter)
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => downloadPDF(openView.id, "triplicate")} data-testid="act-pdf-tri">
                        <Copy className="w-4 h-4 mr-2" /> PDF · Triplicate (Supplier)
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => setShowReminder("invoice")} data-testid="act-send-email">
                        <Mail className="w-4 h-4 mr-2" /> Send by E-Mail
                      </DropdownMenuItem>
                      {openView.status !== "cancelled" && (
                      <DropdownMenuItem onClick={() => { setShowReminder("reminder"); }} data-testid="act-reminder">
                        <Bell className="w-4 h-4 mr-2" /> Send Reminder
                      </DropdownMenuItem>
                      )}
                      {openView.status !== "cancelled" && (<>
                      <DropdownMenuItem onClick={() => setShowEdit(true)} data-testid="act-edit">
                        <PenSquare className="w-4 h-4 mr-2" /> Edit
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setShowReceipt(true)} data-testid="act-receipt">
                        <Receipt className="w-4 h-4 mr-2" /> Add Receipt
                      </DropdownMenuItem>
                      </>)}
                      {canCancel && openView.status !== "cancelled" && (<>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => setShowCancel(true)} className="text-red-600" data-testid="act-cancel-invoice">
                          <FileMinus className="w-4 h-4 mr-2" /> Cancel invoice
                        </DropdownMenuItem>
                      </>)}
                      {openView.status !== "cancelled" && (<>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => setShowCreditNote(true)} data-testid="act-credit-note">
                        <FileMinus className="w-4 h-4 mr-2" /> Create Credit Note
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setShowRecurring(true)} data-testid="act-recurring">
                        <Repeat className="w-4 h-4 mr-2" /> Convert to Recurring
                      </DropdownMenuItem>
                      </>)}
                      <DropdownMenuSeparator />
                      {!einvoice?.irn && openView.status !== "cancelled" && (
                        <DropdownMenuItem onClick={() => genEInvoice(openView.id)} data-testid="act-irn">
                          <QrCode className="w-4 h-4 mr-2" /> Generate IRN
                        </DropdownMenuItem>
                      )}
                      {einvoice?.irn && !einvoice?.ewb_no && openView.status !== "cancelled" && (
                        <DropdownMenuItem onClick={() => genEwayBill(openView.id)} data-testid="act-ewb">
                          <FileText className="w-4 h-4 mr-2" /> Generate E-Way Bill
                        </DropdownMenuItem>
                      )}
                      {einvoice?.irn && einvoice?.einvoice_status !== "cancelled" && (
                        <DropdownMenuItem onClick={() => cancelEInvoice(openView.id)} className="text-red-600" data-testid="act-cancel-irn">
                          Cancel IRN
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>

              {/* On-screen invoice preview — matches PDF layout */}
              <div className="p-4 md:p-6 bg-muted/30">
                <InvoicePreview
                  invoice={openView}
                  org={org || {}}
                  customer={openView.customer || {}}
                  einvoice={einvoice}
                  qrUrl={qrUrl}
                />

                <div className="mt-6">
                  <Attachments docType="sales_invoice" docId={openView.id} />
                </div>
                <InvoiceEmailHistory invoiceId={openView.id} refreshKey={showReminder ? 0 : 1} />

                <div className="mt-6">
                  <DSCSigningPanel
                    docType="sales_invoice"
                    docId={openView.id}
                    downloadUnsignedUrl={`${API}/sales/invoices/${openView.id}/cgst-invoice.pdf?copy=original`}
                    downloadUnsignedLabel="Download unsigned invoice PDF"
                    onSigned={() => load?.()}
                  />
                </div>
                <div className="mt-3">
                  <ESignButton
                    pdfPath={`/sales/invoices/${openView.id}/cgst-invoice.pdf?copy=original`}
                    fileName={`Invoice-${openView.invoice_number}.pdf`}
                    docType="sales_invoice" docRef={openView.id}
                    title={`Invoice ${openView.invoice_number}`}
                    testId="invoice-esign-btn"
                  />
                </div>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* ADD RECEIPT DIALOG */}
      <Dialog open={showReceipt} onOpenChange={setShowReceipt}>
        <DialogContent className="max-w-md" data-testid="receipt-dialog">
          <DialogHeader><DialogTitle>Record Receipt · {openView?.invoice_number}</DialogTitle></DialogHeader>
          <ReceiptForm
            invoice={openView}
            bankAccounts={bankAccounts}
            onDone={() => { setShowReceipt(false); load(); openDetail(openView.id); }}
          />
        </DialogContent>
      </Dialog>

      {/* EDIT DIALOG (header fields only — no line edits) */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className="max-w-lg" data-testid="edit-dialog">
          <DialogHeader><DialogTitle>Edit Invoice · {openView?.invoice_number}</DialogTitle></DialogHeader>
          <EditForm
            invoice={openView}
            onDone={() => { setShowEdit(false); openDetail(openView.id); load(); }}
          />
        </DialogContent>
      </Dialog>

      {/* REMINDER / SEND-BY-EMAIL DIALOG */}
      <Dialog open={!!showReminder} onOpenChange={(o) => !o && setShowReminder(false)}>
        <DialogContent className="max-w-lg" data-testid="reminder-dialog">
          <DialogHeader><DialogTitle>{showReminder === "invoice" ? "Send Invoice by E-Mail" : "Send Payment Reminder"} · {openView?.invoice_number}</DialogTitle></DialogHeader>
          <ReminderForm
            invoice={openView}
            mode={showReminder === "invoice" ? "invoice" : "reminder"}
            onDone={() => setShowReminder(false)}
          />
        </DialogContent>
      </Dialog>

      {/* CREDIT NOTE DIALOG */}
      <Dialog open={showCreditNote} onOpenChange={setShowCreditNote}>
        <DialogContent className="max-w-md" data-testid="credit-note-dialog">
          <DialogHeader><DialogTitle>Create Credit Note · Reverse {openView?.invoice_number}</DialogTitle></DialogHeader>
          <CreditNoteForm invoice={openView}
            onDone={() => { setShowCreditNote(false); openDetail(openView.id); load(); }} />
        </DialogContent>
      </Dialog>

      {/* CONVERT TO RECURRING DIALOG */}
      <Dialog open={showRecurring} onOpenChange={setShowRecurring}>
        <DialogContent className="max-w-md" data-testid="recurring-dialog">
          <DialogHeader><DialogTitle>Convert to Recurring · {openView?.invoice_number}</DialogTitle></DialogHeader>
          <RecurringForm invoice={openView}
            onDone={() => setShowRecurring(false)} />
        </DialogContent>
      </Dialog>

      {/* OUTWARD-DISPATCH QC MODAL */}
      {openView && (
        <QCRecordModal
          open={showQC}
          onOpenChange={setShowQC}
          kind="outward"
          sourceId={openView.id}
          sourceRef={openView.invoice_number}
          onCreated={() => setShowQC(false)}
        />
      )}
    </div>
  );
}

// ─── Receipt form ───────────────────────────────────────────────────────
function ReceiptForm({ invoice, bankAccounts, onDone }) {
  const [form, setForm] = useState({
    date: today(),
    bank_account_id: bankAccounts[0]?.id || "",
    amount: invoice?.balance_due ?? invoice?.grand_total ?? 0,
    reference: "",
    narration: `Receipt against ${invoice?.invoice_number || ""}`,
  });
  useEffect(() => {
    setForm((f) => ({ ...f, amount: invoice?.balance_due ?? invoice?.grand_total ?? 0,
                     narration: `Receipt against ${invoice?.invoice_number || ""}` }));
  }, [invoice]);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!form.bank_account_id) return toast.error("Select a bank / cash account");
    if (!(form.amount > 0)) return toast.error("Enter a positive amount");
    setBusy(true);
    try {
      await api.post("/payments", {
        kind: "receipt", date: form.date,
        party_id: invoice.customer_id,
        bank_account_id: form.bank_account_id,
        amount: Number(form.amount),
        allocations: [{ doc_type: "sales_invoice", doc_id: invoice.id, amount: Number(form.amount) }],
        reference: form.reference, narration: form.narration,
      });
      toast.success("Receipt posted"); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-sm mt-1" data-testid="rc-date" /></div>
        <div><Label>Amount (INR)</Label><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="rounded-sm mt-1 text-right tabular-nums font-mono" data-testid="rc-amount" /></div>
      </div>
      <div><Label>Received Into</Label>
        <Select value={form.bank_account_id} onValueChange={(v) => setForm({ ...form, bank_account_id: v })}>
          <SelectTrigger className="rounded-sm mt-1" data-testid="rc-account"><SelectValue placeholder="Pick bank / cash" /></SelectTrigger>
          <SelectContent>{bankAccounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name} · {a.subtype}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div><Label>Reference (UTR / Cheque #)</Label><Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} className="rounded-sm mt-1" data-testid="rc-ref" /></div>
      <div><Label>Narration</Label><Input value={form.narration} onChange={(e) => setForm({ ...form, narration: e.target.value })} className="rounded-sm mt-1" data-testid="rc-narration" /></div>
      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" onClick={onDone} className="rounded-sm">Cancel</Button>
        <Button onClick={submit} disabled={busy} className="rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="rc-submit">{busy ? "Posting…" : "Post Receipt"}</Button>
      </div>
    </div>
  );
}

// ─── Edit form ───────────────────────────────────────────────────────
function EditForm({ invoice, onDone }) {
  const [form, setForm] = useState({
    notes: invoice?.notes || "",
    terms: invoice?.terms || "Payment due within 30 days.",
    due_date: invoice?.due_date || "",
    reverse_charge: !!invoice?.reverse_charge,
    place_of_supply: invoice?.place_of_supply || "",
  });
  useEffect(() => setForm({
    notes: invoice?.notes || "",
    terms: invoice?.terms || "Payment due within 30 days.",
    due_date: invoice?.due_date || "",
    reverse_charge: !!invoice?.reverse_charge,
    place_of_supply: invoice?.place_of_supply || "",
  }), [invoice]);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.patch(`/sales/invoices/${invoice.id}`, form);
      toast.success("Invoice updated"); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  if (!invoice) return null;
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Place of Supply</Label><Input value={form.place_of_supply} onChange={(e) => setForm({ ...form, place_of_supply: e.target.value })} className="rounded-sm mt-1" data-testid="ed-pos" /></div>
        <div><Label>Due Date</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="rounded-sm mt-1" data-testid="ed-due" /></div>
      </div>
      <label className="flex items-center gap-3 cursor-pointer border rounded-sm p-2 bg-muted/30">
        <Switch checked={form.reverse_charge} onCheckedChange={(v) => setForm({ ...form, reverse_charge: v })} data-testid="ed-rcm" />
        <div><div className="text-sm font-medium">Reverse Charge</div><div className="text-[10px] text-muted-foreground">Recipient discharges GST liability</div></div>
      </label>
      <div><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-sm mt-1 min-h-[60px]" data-testid="ed-notes" /></div>
      <div><Label>Terms &amp; Conditions</Label><Textarea value={form.terms} onChange={(e) => setForm({ ...form, terms: e.target.value })} className="rounded-sm mt-1 min-h-[60px]" data-testid="ed-terms" /></div>
      <div className="text-[11px] text-muted-foreground italic">Line-level edits (qty / rate / discount) are not supported after posting — delete &amp; re-post to change amounts (keeps the audit trail clean).</div>
      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" onClick={onDone} className="rounded-sm">Cancel</Button>
        <Button onClick={submit} disabled={busy} className="rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="ed-submit">{busy ? "Saving…" : "Save Changes"}</Button>
      </div>
    </div>
  );
}

// ─── Credit Note form (partial or full reversal) ───────────────────────────
function CreditNoteForm({ invoice, onDone }) {
  const [form, setForm] = useState({ amount_pct: 100, reason: "sales_return", notes: "", date: today() });
  useEffect(() => setForm((f) => ({ ...f, notes: `Reversal of invoice ${invoice?.invoice_number || ""}` })), [invoice]);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!(form.amount_pct > 0 && form.amount_pct <= 100)) return toast.error("Enter a percentage between 0 and 100");
    setBusy(true);
    try {
      const r = await api.post(`/sales/invoices/${invoice.id}/credit-note`, form);
      toast.success(`Credit Note ${r.data.note_number} posted · ${fmtINR(r.data.grand_total)} reversed`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  if (!invoice) return null;
  const est = Number(invoice.grand_total || 0) * Number(form.amount_pct || 0) / 100;
  return (
    <div className="space-y-3">
      <div className="text-xs text-muted-foreground">Original invoice total: <b>{fmtINR(invoice.grand_total)}</b></div>
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2"><DocNumberField kind="credit_note" label="Credit Note No." open={!!invoice} branchId={invoice?.branch_id}
          value={form.note_number} testId="cn-number" onChange={(v) => setForm((f) => ({ ...f, note_number: v }))} /></div>
        <div><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-sm mt-1" data-testid="cn-date" /></div>
        <div><Label>Reverse %</Label><Input type="number" min="1" max="100" value={form.amount_pct} onChange={(e) => setForm({ ...form, amount_pct: Number(e.target.value) })} className="rounded-sm mt-1 text-right tabular-nums" data-testid="cn-pct" /></div>
      </div>
      <div><Label>Reason</Label>
        <Select value={form.reason} onValueChange={(v) => setForm({ ...form, reason: v })}>
          <SelectTrigger className="rounded-sm mt-1" data-testid="cn-reason"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="sales_return">Sales Return</SelectItem>
            <SelectItem value="discount">Discount / Rebate</SelectItem>
            <SelectItem value="correction">Correction</SelectItem>
            <SelectItem value="other">Other</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="rounded-sm mt-1 min-h-[60px]" data-testid="cn-notes" /></div>
      <div className="border rounded-sm p-2.5 bg-muted/40 text-xs flex items-center justify-between">
        <span className="text-muted-foreground">Estimated credit</span>
        <span className="font-mono font-semibold text-red-600">- {fmtINR(est)}</span>
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <Button variant="outline" onClick={onDone} className="rounded-sm">Cancel</Button>
        <Button onClick={submit} disabled={busy} className="rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="cn-submit">{busy ? "Posting…" : "Post Credit Note"}</Button>
      </div>
    </div>
  );
}

// ─── Convert-to-Recurring form ───────────────────────────
function RecurringForm({ invoice, onDone }) {
  const [form, setForm] = useState({
    name: "", cadence: "monthly",
    start_date: today(), end_date: "", max_occurrences: "",
  });
  useEffect(() => setForm((f) => ({ ...f,
    name: `Recurring · ${invoice?.invoice_number || ""} · ${invoice?.customer?.name || ""}`.trim(),
  })), [invoice]);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const payload = { ...form };
      if (!payload.end_date) delete payload.end_date;
      if (!payload.max_occurrences) delete payload.max_occurrences;
      else payload.max_occurrences = Number(payload.max_occurrences);
      const r = await api.post(`/sales/invoices/${invoice.id}/convert-to-recurring`, payload);
      toast.success(`Recurring "${r.data.name}" scheduled · next run ${r.data.next_run_date}`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  if (!invoice) return null;
  return (
    <div className="space-y-3">
      <div className="text-xs text-muted-foreground">Auto-draft the same invoice (same lines, GST, discount) on the schedule below. Toggle it off any time from the Recurring page.</div>
      <div><Label>Template name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm mt-1" data-testid="rec-name" /></div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Cadence</Label>
          <Select value={form.cadence} onValueChange={(v) => setForm({ ...form, cadence: v })}>
            <SelectTrigger className="rounded-sm mt-1" data-testid="rec-cadence"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="weekly">Weekly</SelectItem>
              <SelectItem value="monthly">Monthly (AMC)</SelectItem>
              <SelectItem value="quarterly">Quarterly</SelectItem>
              <SelectItem value="yearly">Yearly</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div><Label>Start date</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="rounded-sm mt-1" data-testid="rec-start" /></div>
        <div><Label>End date (optional)</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="rounded-sm mt-1" data-testid="rec-end" /></div>
        <div><Label>Max occurrences</Label><Input type="number" placeholder="Unlimited" value={form.max_occurrences} onChange={(e) => setForm({ ...form, max_occurrences: e.target.value })} className="rounded-sm mt-1" data-testid="rec-max" /></div>
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <Button variant="outline" onClick={onDone} className="rounded-sm">Cancel</Button>
        <Button onClick={submit} disabled={busy} className="rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="rec-submit">{busy ? "Saving…" : "Create Recurring"}</Button>
      </div>
    </div>
  );
}

// ─── Reminder / Send-by-Email form ───────────────────────────────────────
function ReminderForm({ invoice, onDone, mode = "reminder" }) {
  const [form, setForm] = useState({ to: "", cc: "", bcc: "", subject: "", message: "" });
  const [showCcBcc, setShowCcBcc] = useState(false);
  useEffect(() => {
    if (!invoice) return;
    const bal = invoice.balance_due ?? invoice.grand_total ?? 0;
    const name = invoice.customer?.name || "Customer";
    const prefs = invoice.customer?.email_prefs || {};
    const join = (a) => (a || []).join(", ");
    const rcpt = { to: join(prefs.to) || invoice.customer?.email || "", cc: join(prefs.cc), bcc: join(prefs.bcc) };
    setShowCcBcc(!!(rcpt.cc || rcpt.bcc));
    const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;
    setForm(mode === "invoice" ? {
      ...rcpt,
      subject: `Invoice ${invoice.invoice_number} dated ${invoice.date}`,
      message: `Dear ${name},\n\nPlease find attached Invoice ${invoice.invoice_number} dated ${invoice.date} for ${inr(invoice.grand_total)}.\n\nBank details are printed on the invoice.\n\nRegards,`,
    } : {
      ...rcpt,
      subject: `Payment Reminder — Invoice ${invoice.invoice_number}`,
      message:
        `Dear ${name},\n\n` +
        `This is a friendly reminder that Invoice ${invoice.invoice_number} dated ${invoice.date} ` +
        `has an outstanding balance of ${inr(bal)}.\n\n` +
        `Kindly arrange payment at your earliest convenience. Bank details are on the attached invoice.\n\n` +
        `Thank you.`,
    });
  }, [invoice, mode]);
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!form.to?.includes("@")) return toast.error("Enter at least one 'To' email");
    setBusy(true);
    try {
      const r = await api.post(`/sales/invoices/${invoice.id}/reminder`, { ...form, kind: mode });
      if (r.data.ok) { toast.success(`Email sent · PDF attached${r.data.contacts_added ? ` · ${r.data.contacts_added} new contact(s) saved` : ""}`); onDone(); }
      else toast.error(r.data.error || "Send failed");
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
    finally { setBusy(false); }
  };
  if (!invoice) return null;
  return (
    <div className="space-y-3">
      <div><div className="flex items-center justify-between"><Label>To * <span className="text-[10px] text-muted-foreground font-normal">(separate several with commas)</span></Label>
        {!showCcBcc && <button type="button" className="text-[11px] text-accent underline" onClick={() => setShowCcBcc(true)} data-testid="rm-show-ccbcc">Add CC / BCC</button>}</div>
        <Input value={form.to} onChange={(e) => setForm({ ...form, to: e.target.value })} className="rounded-sm mt-1" data-testid="rm-to" placeholder="customer@example.in, accounts@example.in" /></div>
      {showCcBcc && (<>
        <div><Label>CC</Label><Input value={form.cc} onChange={(e) => setForm({ ...form, cc: e.target.value })} className="rounded-sm mt-1" data-testid="rm-cc" placeholder="finance@example.in" /></div>
        <div><Label>BCC</Label><Input value={form.bcc} onChange={(e) => setForm({ ...form, bcc: e.target.value })} className="rounded-sm mt-1" data-testid="rm-bcc" placeholder="Hidden copy, e.g. your own email" /></div>
      </>)}
      <SavedContacts invoice={invoice} form={form} setForm={setForm} onAdd={() => setShowCcBcc(true)} />
      <div><Label>Subject</Label><Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="rounded-sm mt-1" data-testid="rm-subject" /></div>
      <div><Label>Message</Label><Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="rounded-sm mt-1 min-h-[140px]" data-testid="rm-message" /></div>
      <div className="text-[11px] text-muted-foreground italic">The tax invoice PDF (with bank details) is attached automatically. New addresses are saved to this customer's contacts and filled in next time.</div>
      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" onClick={onDone} className="rounded-sm">Cancel</Button>
        <Button onClick={send} disabled={busy} className="rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="rm-send">{busy ? "Sending…" : "Send Now"}</Button>
      </div>
    </div>
  );
}

function SavedContacts({ invoice, form, setForm, onAdd }) {
  const used = `${form.to},${form.cc},${form.bcc}`.toLowerCase();
  const emails = [invoice?.customer?.email, ...((invoice?.customer?.contacts || []).map((c) => c.email))]
    .filter((e) => e && !used.includes(e.toLowerCase()));
  const uniq = [...new Set(emails)];
  if (!uniq.length) return null;
  const addCc = (e) => { onAdd(); setForm((f) => ({ ...f, cc: f.cc ? `${f.cc}, ${e}` : e })); };
  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid="rm-saved-contacts">
      <span className="text-[10px] font-mono uppercase text-muted-foreground">Saved contacts · click to CC</span>
      {uniq.map((e) => (
        <button key={e} type="button" onClick={() => addCc(e)} className="text-[11px] px-2 py-0.5 border rounded-full hover:bg-muted" data-testid={`rm-contact-${e}`}>{e}</button>
      ))}
    </div>
  );
}

